/**
 * Authentication type definitions matching backend models.
 *
 * These types ensure type safety for authentication-related data
 * flowing between frontend and backend.
 */

/**
 * Permission enum matching backend Permission (app/models/permissions.py).
 *
 * Each permission represents a specific action or capability within the system.
 */
export const Permission = {
  MANAGE_ALL_ORGANIZATIONS: 'manage_all_organizations',
  MANAGE_ORG_MEMBERSHIP: 'manage_org_membership',
  MANAGE_ALL_TENANTS: 'manage_all_tenants',
  REQUEST_TENANT_CHANGES: 'request_tenant_changes',
  VIEW_ORG_TENANTS: 'view_org_tenants',
  MANAGE_TENANT_ACCESS: 'manage_tenant_access',
  MANAGE_TENANT_USERS: 'manage_tenant_users',
  MANAGE_TENANT_SECURITY: 'manage_tenant_security',
  MANAGE_LLM_SETTINGS: 'manage_llm_settings',
  ACCESS_APPS: 'access_apps',
} as const;

export type PermissionValue = (typeof Permission)[keyof typeof Permission];

/**
 * User role enum matching backend UserRole (app/models/auth.py).
 *
 * Role hierarchy (highest to lowest):
 * 1. global_admin - Full system access, can manage all organizations
 * 2. organization_owner - Owns an organization, can manage org settings and users
 * 3. organization_admin - Admin for an organization, can manage org users
 * 4. organization_user - Regular user within an organization
 * 5. managed_tenant_user - User in a managed tenant (default role)
 */
export type UserRole =
  | 'global_admin'
  | 'organization_owner'
  | 'organization_admin'
  | 'organization_user'
  | 'managed_tenant_user';

/**
 * User model matching backend User model (app/models/auth.py).
 *
 * Represents an authenticated Azure AD user with role-based permissions.
 */
export interface User {
  id: number;
  email: string;
  name: string;
  azure_object_id: string;
  user_principal_name: string | null;
  is_active: boolean;
  role: UserRole;
  tenant_id: string | null;
  organization_id: number | null;
  created_at: string;
  last_login: string | null;
  permissions: string[];
}

/**
 * Helper function to check if user has global admin role.
 * Used for UI logic (showing/hiding elements based on role).
 */
export function isGlobalAdmin(user: User | null): boolean {
  return user?.role === 'global_admin';
}

/**
 * Helper function to check if user has a specific permission.
 *
 * @param user - The user to check, or null
 * @param permission - The permission to check for
 * @returns true if the user has the permission, false otherwise
 */
export function hasPermission(
  user: User | null,
  permission: PermissionValue
): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

/**
 * Helper function to check if user has any of the specified permissions.
 *
 * @param user - The user to check, or null
 * @param permissions - The permissions to check for (any match succeeds)
 * @returns true if the user has at least one of the permissions, false otherwise
 */
export function hasAnyPermission(
  user: User | null,
  permissions: PermissionValue[]
): boolean {
  if (!user?.permissions || permissions.length === 0) return false;
  return permissions.some((permission) => user.permissions.includes(permission));
}

/**
 * Token response model matching backend TokenResponse (app/models/auth.py).
 *
 * Returned after successful authentication or token refresh.
 */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

/**
 * Login request model matching backend LoginRequest (app/models/auth.py).
 *
 * Sent to exchange OAuth authorization code for tokens.
 */
export interface LoginRequest {
  authorization_code: string;
  redirect_uri: string;
}

/**
 * Refresh token request model matching backend RefreshTokenRequest (app/models/auth.py).
 *
 * Sent to refresh access token using refresh token.
 */
export interface RefreshTokenRequest {
  refresh_token: string;
}
